// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button, TextField, Box, Typography, useTheme, Link, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment, FormControlLabel, Checkbox } from '@mui/material';
import { ColorModeToggleButton } from '../theme/themeContext';
import { useNavigate } from 'react-router-dom';
import loginVideo from '../assets/LoginVideo.mp4';
import { supabase } from '../supabase';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
  const theme = useTheme();
  const navigate = useNavigate();

  // Check for saved credentials on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleRememberMeChange = (event) => {
    setRememberMe(event.target.checked);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Handle remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      // Sign in with session persistence based on remember me choice
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          // Set persistence based on remember me choice
          // 'local' = persists session even after browser close
          // 'session' = clears on tab close
          persistSession: true,
          storeSession: rememberMe ? 'local' : 'session'
        }
      });

      if (error) throw error;
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
      setErrorDialog({
        open: true,
        message: error.message || 'Login failed. Please check your credentials.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setErrorDialog({
        open: true,
        message: 'Please enter your email address.'
      });
      return;
    }
    setLoading(true);
    try {
      // Use the production URL for the reset password page
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://uptime-tracker-2ba03.web.app/reset-password',
      });
      if (error) throw error;
      setErrorDialog({
        open: true,
        message: 'Password reset email sent successfully. Check your inbox.'
      });
    } catch (error) {
      console.error(error);
      setErrorDialog({
        open: true,
        message: 'Failed to send password reset email.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseErrorDialog = () => {
    setErrorDialog({ ...errorDialog, open: false });
  };

  const boxBackground = theme.palette.mode === 'dark' ? '#121212' : '#fafafa';

  return (
    <>
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Video background */}
        <video
          autoPlay
          muted
          loop
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: -2,
          }}
        >
          <source src={loginVideo} type="video/mp4" />
        </video>

        {/* Dark overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black overlay
            zIndex: -1,
          }}
        />

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '600px',
          bgcolor: boxBackground,
          borderRadius: '15px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)', // Enhanced shadow for better depth
        }}>
          <Typography variant="h4" component="h4" sx={{ mb: 4 }}>
            <span className="montserrat">GLS</span>
            <span className="yellow-dot montserrat">.</span>
            <span className="bold-text">UPTIME TRACKER</span>
          </Typography>

          <TextField
            InputLabelProps={{ shrink: true }}
            label="Email"
            variant="outlined"
            margin="normal"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            InputLabelProps={{ shrink: true }}
            label="Password"
            variant="outlined"
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  color="primary"
                  disabled={loading}
                />
              }
              label="Remember me"
            />
            <Link
              component="button"
              variant="body2"
              onClick={handleForgotPassword}
              sx={{ cursor: 'pointer' }}
              disabled={loading}
              type="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            >
              Forgot Password?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{ mt: 2 }}
          >
            Login
          </Button>
        </Box>
      </Box>

      <Dialog
        open={errorDialog.open}
        onClose={handleCloseErrorDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {errorDialog.message.includes('success') ? 'Success' : 'Error'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {errorDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ position: 'fixed', top: 16, right: 16 }}>
        <ColorModeToggleButton />
      </Box>
    </>
  );
};

export default LoginPage;